// FILE: src/pages/admin/AdminAccommodationDetailPage.jsx
// One accommodation request (MTO 12.3; AODA): /admin/accommodations/:id,
// the page the staff notification email links to. Loaded from
// GET /api/admin/accommodations/:id (404 ACCOMMODATION_NOT_FOUND); staff
// change its status and add notes here (audited on the server), and see
// every status change with its date.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { Box, Button, Stack, Typography } from "@mui/material";
import AccessibilityNewOutlinedIcon from "@mui/icons-material/AccessibilityNewOutlined";
import fetchWithAuth from "../../utils/fetchWithAuth";
import { DetailList, EmptyState, ErrorState, LoadingState, PageHeader, Section } from "../../components/ui";
import AccommodationRequestCard, {
  ACCOMMODATION_STATUS,
  preferredContactText
} from "../../components/admin/AccommodationRequestCard";
import { formatDateTime } from "../../components/admin/format";
import { errorCode, messageFor } from "../../i18n/errors";
import { humanize } from "../../i18n/labels";

const LIST = "/admin/accommodations";

export default function AdminAccommodationDetailPage() {
  const { id } = useParams();
  const [state, setState] = useState({ item: null, loading: true, error: null });
  const latest = useRef(0);

  const load = useCallback(() => {
    const req = ++latest.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    fetchWithAuth(`/api/admin/accommodations/${encodeURIComponent(id)}`)
      .then((item) => {
        if (req === latest.current) setState({ item, loading: false, error: null });
      })
      .catch((error) => {
        if (req === latest.current) setState({ item: null, loading: false, error: error || "INTERNAL_ERROR" });
      });
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const { item, loading, error } = state;
  const breadcrumbs = [
    { label: "Accommodation requests", to: LIST },
    { label: item?.reference ? `Request ${item.reference}` : "Request" }
  ];

  if (!item) {
    if (loading) return <LoadingState variant="page" label="Loading the request…" />;
    const missing = ["ACCOMMODATION_NOT_FOUND", "NOT_FOUND"].includes(errorCode(error));
    return (
      <Box>
        <PageHeader title="Accommodation request" breadcrumbs={breadcrumbs} />
        {missing ? (
          <EmptyState
            icon={AccessibilityNewOutlinedIcon}
            title={messageFor("ACCOMMODATION_NOT_FOUND")}
            description="Check the link, or go back to the list to find the request."
            action={
              <Button component={RouterLink} to={LIST} variant="contained">
                Back to accommodation requests
              </Button>
            }
          />
        ) : (
          <ErrorState error={error} title="We couldn't load this request" onRetry={load} />
        )}
      </Box>
    );
  }

  const history = Array.isArray(item.statusHistory) ? item.statusHistory.filter((h) => h && h.at) : [];

  return (
    <Box>
      <PageHeader
        eyebrow={item.reference ? `Reference ${item.reference}` : undefined}
        title={`Accommodation request from ${item.name}`}
        breadcrumbs={breadcrumbs}
        description={`Reply by ${preferredContactText(item.preferredContact)}, then close the request.`}
        actions={
          <Button component={RouterLink} to={LIST} variant="outlined">
            Back to accommodation requests
          </Button>
        }
      />

      <Box
        sx={{
          display: "grid",
          gap: 2.5,
          alignItems: "start",
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.7fr) minmax(0, 1fr)" }
        }}
      >
        <AccommodationRequestCard
          item={item}
          detailed
          onSaved={(updated) => {
            if (updated?._id) setState({ item: updated, loading: false, error: null });
          }}
        />

        <Stack spacing={2.5}>
          <Section id="dates" title="Dates">
            <DetailList
              columns={1}
              items={[
                { label: "Received", value: formatDateTime(item.createdAt) },
                { label: "Last updated", value: formatDateTime(item.updatedAt) }
              ]}
            />
          </Section>
          <Section id="history" title="Status changes">
            {history.length === 0 ? (
              <Typography sx={{ color: "text.secondary" }}>The status hasn't changed since the request arrived.</Typography>
            ) : (
              <Box component="ol" aria-label="Status changes, oldest first" sx={{ m: 0, pl: 2.5 }}>
                {history.map((h, i) => (
                  <Typography component="li" key={`${h.at}-${i}`} sx={{ mb: 1 }}>
                    <Box component="span" sx={{ fontWeight: 600 }}>
                      {ACCOMMODATION_STATUS[h.status]?.label || humanize(h.status)}
                    </Box>
                    <Box component="span" sx={{ display: "block", color: "text.secondary" }}>
                      {formatDateTime(h.at)}
                    </Box>
                  </Typography>
                ))}
              </Box>
            )}
          </Section>
        </Stack>
      </Box>
    </Box>
  );
}
